import React, { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import {
  Sparkles, CheckCircle2, Clock, XCircle, Calendar, MapPin, Building2, ArrowRight, Plus,
} from "lucide-react";

const StatusBadge = ({ status, t }) => {
  const map = {
    nieuw: { label: t("mijn_matches.status_nieuw"), cls: "bg-amber-100 text-[#B7740A]", Icon: Clock },
    gesprek_gepland: { label: t("mijn_matches.status_gesprek"), cls: "bg-blue-100 text-blue-800", Icon: Calendar },
    match: { label: t("mijn_matches.status_match"), cls: "bg-emerald-100 text-emerald-800", Icon: CheckCircle2 },
    geen_match: { label: t("mijn_matches.status_no_match"), cls: "bg-rose-100 text-rose-800", Icon: XCircle },
  };
  const m = map[status] || map.nieuw;
  const Icon = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${m.cls}`}>
      <Icon size={12} /> {m.label}
    </span>
  );
};

const KlantCard = ({ klant, t, lang }) => {
  return (
    <div className="beecenter-card p-6" data-testid={`klant-card-${klant.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-xl font-bold text-[#1A2B4A]">{klant.bedrijfsnaam}</h2>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500">
            <span className="flex items-center gap-1"><Building2 size={12} /> {klant.rechtsvorm}</span>
            <span>·</span>
            <span>{klant.branche}</span>
            <span>·</span>
            <span className="flex items-center gap-1"><MapPin size={12} /> {klant.locatie}</span>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${klant.betaald ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
          {klant.betaald ? t("mijn_matches.paid") : t("mijn_matches.not_paid")}
        </span>
      </div>

      {!klant.betaald && (
        <div className="mt-5 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <Clock size={18} className="text-[#F5A623] flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-[#7a4d05]">{t("mijn_matches.complete_payment")}</p>
            <p className="text-xs text-[#7a4d05] mt-0.5">{t("mijn_matches.complete_payment_sub")}</p>
          </div>
          <Link to={`/onboarding?step=4&klant_id=${klant.id}`} className="btn-amber !py-2 !px-4 text-xs whitespace-nowrap" data-testid={`btn-pay-${klant.id}`}>
            {t("mijn_matches.pay_btn")}
          </Link>
        </div>
      )}

      {klant.betaald && klant.matches?.length > 0 && (
        <div className="mt-6">
          <h3 className="text-xs uppercase tracking-wider text-slate-500 font-bold">{t("mijn_matches.matches_count", { count: klant.matches.length })}</h3>
          <div className="mt-3 space-y-3">
            {klant.matches.map((m) => (
              <div key={m.id} className="flex items-center gap-4 p-4 rounded-xl border border-slate-200 bg-slate-50/50" data-testid={`match-row-${m.id}`}>
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-white ring-1 ring-slate-200 flex-shrink-0">
                  {m.kantoor?.logo_url ? (
                    <img src={m.kantoor.logo_url} alt={m.kantoor.naam} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#1A2B4A] font-bold">{m.kantoor?.naam?.[0]}</div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#1A2B4A] truncate">{m.kantoor?.naam}</div>
                  <div className="text-xs text-slate-500 truncate">{(m.kantoor?.specialismes || []).slice(0, 2).join(" · ")}</div>
                  {m.gesprek && (
                    <div className="mt-1 text-xs text-blue-700 flex items-center gap-1">
                      <Calendar size={11} /> {t("mijn_matches.gesprek_label")} {new Date(m.gesprek.datum).toLocaleDateString(lang, { day: "numeric", month: "short" })} · {m.gesprek.tijdstip}
                    </div>
                  )}
                </div>
                <StatusBadge status={m.status} t={t} />
              </div>
            ))}
          </div>
        </div>
      )}

      {klant.betaald && (!klant.matches || klant.matches.length === 0) && (
        <div className="mt-5 text-sm text-slate-500 italic">{t("mijn_matches.preparing")}</div>
      )}
    </div>
  );
};

const MijnMatches = () => {
  const { t, i18n } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [klanten, setKlanten] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login?role=klant"); return; }
    if (user.role === "kantoor") { navigate("/dashboard"); return; }
    api.get("/klanten/me").then((r) => setKlanten(r.data)).finally(() => setLoading(false));
  }, [user, authLoading, navigate]);

  const lang = i18n.language?.startsWith("en") ? "en-US" : "nl-NL";
  const namePart = user?.name ? `, ${user.name.split(" ")[0]}` : "";

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-1 bg-[#F8FAFC]">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-10 md:py-14">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <span className="pill-amber">{t("mijn_matches.badge")}</span>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#1A2B4A] mt-3">{t("mijn_matches.title")}</h1>
              <p className="text-slate-600 mt-1">{t("mijn_matches.welcome", { name: namePart })}</p>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/onboarding" className="btn-amber" data-testid="new-aanvraag-btn">
                <Plus size={16} /> {t("mijn_matches.new_request")}
              </Link>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-6 h-6 border-2 border-[#F5A623] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : klanten.length === 0 ? (
            <div className="mt-10 beecenter-card p-12 text-center" data-testid="empty-state">
              <Sparkles className="mx-auto text-[#F5A623]" size={36} />
              <h2 className="font-heading text-2xl font-bold text-[#1A2B4A] mt-4">{t("mijn_matches.empty_title")}</h2>
              <p className="text-slate-600 mt-2 max-w-md mx-auto">{t("mijn_matches.empty_desc")}</p>
              <Link to="/onboarding" className="btn-amber mt-6 inline-flex" data-testid="empty-cta">
                {t("mijn_matches.empty_cta")} <ArrowRight size={16} />
              </Link>
            </div>
          ) : (
            <div className="mt-8 space-y-6">
              {klanten.map((k) => <KlantCard key={k.id} klant={k} t={t} lang={lang} />)}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default MijnMatches;
